/**
 * VistoeraTASK-00557 — Canvas Audio Picker team folder tree (mirrors image picker).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createAudioFolder,
  deleteAudioFolder,
  listAudioFolders,
  renameAudioFolder,
  type ImageFoldersResponse,
  type SaasApiError,
} from '../../api'
import { isCancelledError } from '../../workspace/imageAssetLoadErrors'

const teamEnabledCache = new Map<string, boolean>()

export type FolderLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export function useAudioPickerFolders(workspaceId: string, open: boolean) {
  const [data, setData] = useState<ImageFoldersResponse | null>(null)
  const [status, setStatus] = useState<FolderLoadStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const seqRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!workspaceId) return
      const seq = ++seqRef.current
      abortRef.current?.abort()
      const abort = new AbortController()
      abortRef.current = abort
      if (!opts?.silent) setStatus('loading')
      try {
        const { data: body } = await listAudioFolders(workspaceId, abort.signal)
        if (seq !== seqRef.current) return
        teamEnabledCache.set(workspaceId, Boolean(body.team?.enabled))
        setData(body)
        setStatus('ready')
        setErrorMessage(null)
      } catch (err) {
        if (seq !== seqRef.current) return
        if (isCancelledError(err)) return
        setStatus('error')
        setErrorMessage((err as SaasApiError).message || null)
      }
    },
    [workspaceId],
  )

  useEffect(() => {
    if (!open) {
      seqRef.current += 1
      abortRef.current?.abort()
      return
    }
    setData(null)
    void reload()
  }, [open, reload])

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      const { data: row } = await createAudioFolder(workspaceId, name, parentId)
      await reload({ silent: true })
      return row
    },
    [workspaceId, reload],
  )
  const renameFolder = useCallback(
    async (folderId: string, name: string) => {
      await renameAudioFolder(workspaceId, folderId, name)
      await reload({ silent: true })
    },
    [workspaceId, reload],
  )
  const deleteFolder = useCallback(
    async (folderId: string) => {
      await deleteAudioFolder(workspaceId, folderId)
      await reload({ silent: true })
    },
    [workspaceId, reload],
  )

  const teamEnabled = data ? Boolean(data.team?.enabled) : (teamEnabledCache.get(workspaceId) ?? false)

  return {
    data,
    status,
    errorMessage,
    teamEnabled,
    folders: data?.collections ?? [],
    canManage: Boolean(data?.can_manage),
    reload,
    createFolder,
    renameFolder,
    deleteFolder,
  }
}
